import { Empty } from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiClient from "../../../../helpers/apiClient";
import displayValue from "../../../../utilities/displayValue";
import CarDetailSection from "../../../cars/components/cardetails/CarDetailSection";
import useUrlParamState from "../../../../hooks/useUrlParamState";
import CarDetailSubTabs from "../../../cars/components/cardetails/CarDetailSubTabs";

function formatSerialId(serialID) {
    if (serialID == null) return "N/A";
    if (serialID < 1000 && serialID > 99) return `0${serialID}`;
    if (serialID < 100 && serialID > 9) return `00${serialID}`;
    return `000${serialID}`;
}

function formatDateTime(value, na) {
    if (!value) return na;
    const date = moment(String(value));
    if (!date.isValid()) return na;
    return `${date.format("MM/DD/YYYY")} ${date.format("hh:mm A")}`;
}

function getLinkedCarLabel(reservation, t) {
    if (reservation.carLinked === "" && !reservation.isRejected && !reservation.isCancelled) {
        return <span className="text-orange-500">{t("rescar.carNotLinked", { ns: "reservations" })}</span>;
    }
    if (reservation.isRejected) {
        return <span className="text-[#FF644B]">{t("rescar.reservationRejected", { ns: "reservations" })}</span>;
    }
    if (reservation.isCancelled) {
        return <span className="text-[#FF644B]">{t("rescar.reservationCancelled", { ns: "reservations" })}</span>;
    }
    return displayValue(reservation.carSelected?.name || reservation.carType, "N/A");
}

function filterByCustomer(reservations, userId, email) {
    if (!Array.isArray(reservations)) return [];
    const normalizedEmail = email?.toLowerCase();
    return reservations.filter(
        (reservation) =>
            reservation.userUID === userId ||
            (normalizedEmail && reservation.email?.toLowerCase() === normalizedEmail),
    );
}

function CustomerReservationsTab({ userId, email, loading: parentLoading = false }) {
    const { t } = useTranslation(["customers", "reservations"]);
    const na = t("common.notAvailable");

    const [upcoming, setUpcoming] = useState([]);
    const [past, setPast] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTabParam, setActive] = useUrlParamState("resTab", 1, [1, 2]);
    const active = Number(activeTabParam);

    const fetchReservations = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                limit: "200",
                page: "1",
                userUID: userId,
            });

            const [upcomingRes, pastRes] = await Promise.all([
                apiClient.get(`/api/reservation/list?${queryParams}`),
                apiClient.get(`/api/past-reservation/list?${queryParams}`),
            ]);

            const upcomingList = filterByCustomer(upcomingRes?.data?.data?.result, userId, email);
            const pastList = filterByCustomer(pastRes?.data?.data?.result, userId, email);

            setUpcoming(upcomingList);
            setPast(pastList);
        } catch {
            setUpcoming([]);
            setPast([]);
        } finally {
            setLoading(false);
        }
    }, [userId, email]);

    useEffect(() => {
        fetchReservations();
    }, [fetchReservations]);

    const currentList = active === 1 ? upcoming : past;
    const isLoading = parentLoading || loading;
    const emptyMessage =
        active === 1 ? t("customerDetail.noUpcomingReservations") : t("customerDetail.noPastReservations");

    const subTabs = [
        { id: 1, label: t("customerDetail.upcomingReservations") },
        { id: 2, label: t("customerDetail.pastReservations") },
    ];

    const renderRow = (reservation) => (
        <tr className="fontRegular bodytt border-b border-[#f0f0f0]" key={reservation.id}>
            <td className="whitespace-nowrap py-3 pr-4">{formatSerialId(reservation.serialID)}</td>
            <td className="whitespace-nowrap py-3 pr-4">{getLinkedCarLabel(reservation, t)}</td>
            <td className="whitespace-nowrap py-3 pr-4">{formatDateTime(reservation.pickupDate, na)}</td>
            <td className="whitespace-nowrap py-3 pr-4">
                {formatDateTime(reservation.dropoffDate || reservation.dropOffDate, na)}
            </td>
            <td className="whitespace-nowrap py-3">
                {reservation.id ? (
                    <Link className="no-underline" to={`/reservations/${reservation.id}`}>
                        <div className="viewbb fontbold">{t("view")}</div>
                    </Link>
                ) : (
                    na
                )}
            </td>
        </tr>
    );

    return (
        <CarDetailSection
            title={t("customerDetail.reservationHistory")}
            loading={isLoading}
            embedded
            className="!mt-0">
            <CarDetailSubTabs tabs={subTabs} active={active} onChange={setActive}>
                {!isLoading && currentList.length === 0 ? (
                    <div className="py-8">
                        <Empty description={emptyMessage} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                ) : (
                    <div className="-mx-2 overflow-x-auto px-2">
                        <table className="sc-table min-w-[560px]">
                            <thead>
                                <tr className="border-b border-[#f0f0f0] text-[#acacb9]">
                                    <th className="whitespace-nowrap pb-2 pr-4 font-normal">
                                        {t("customerDetail.resId")}
                                    </th>
                                    <th className="whitespace-nowrap pb-2 pr-4 font-normal">
                                        {t("customerDetail.linkedCar")}
                                    </th>
                                    <th className="whitespace-nowrap pb-2 pr-4 font-normal">
                                        {t("customerDetail.pickupDate")}
                                    </th>
                                    <th className="whitespace-nowrap pb-2 pr-4 font-normal">
                                        {t("customerDetail.dropoffDate")}
                                    </th>
                                    <th className="pb-2 font-normal" />
                                </tr>
                            </thead>
                            <tbody>{currentList.map(renderRow)}</tbody>
                        </table>
                    </div>
                )}
            </CarDetailSubTabs>
        </CarDetailSection>
    );
}

CustomerReservationsTab.propTypes = {
    userId: PropTypes.string,
    email: PropTypes.string,
    loading: PropTypes.bool,
};

export default CustomerReservationsTab;
